import React, { useEffect, useId, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    LinearProgress,
    Link,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Add,
    CakeOutlined,
    CheckCircle,
    Close,
    ContactPhoneOutlined,
    ContentCopy,
    Delete,
    Edit,
    HomeOutlined,
    LinkOutlined,
    LocationOn,
    LockReset,
    MailOutline,
    ManageAccountsOutlined,
    NotesOutlined,
    PetsOutlined,
    PhoneOutlined,
    ReceiptLongOutlined,
    RemoveCircleOutline,
    TuneOutlined,
} from "@mui/icons-material";
import { getCustomerFullName } from "../../utils/customerUtils";
import { customerService } from "../../services/customerService";
import { petService } from "../../services/petService";
import { Customer, CustomerAddress, Pet } from "../../types";
import { usePermissions } from "../../hooks/usePermissions";
import { PetProfileEditor } from "./PetProfileEditor";
import { LinkedTasks } from "../../pages/tasks/LinkedTasks";

/** What changed inside the dialog itself (Edit is handled by the caller). */
export type CustomerDetailsChange = "addresses" | "pets";

export interface CustomerDetailsModalProps {
    open: boolean;
    onClose: () => void;
    customer: Customer | null;
    /** Shows an Edit button. Called with the customer as currently shown —
     * including addresses and pets changed in this dialog. */
    onEdit?: (customer: Customer) => void;
    /** The customer is being reloaded (e.g. after an edit). */
    refreshing?: boolean;
    /** An address default or a pet was changed here. */
    onChange?: (customer: Customer, change: CustomerDetailsChange) => void;
    /** Shows an "All {name}'s Orders" button. */
    onShowOrders?: (customer: Customer) => void;
}

// ── Formatting ──────────────────────────────────────────────────────────────

/** "30 Aug 2026". Date-only values (birthdays) are read as calendar dates,
 * so they never shift a day with the viewer's time zone. */
function formatDate(value?: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        ...(/^\d{4}-\d{2}-\d{2}$/.test(value) ? { timeZone: "UTC" } : {}),
    });
}

/** "CUSTOMER_SHIPPING" → "Shipping", "ECOMMERCE" → "Ecommerce". */
function humanize(code?: string | null): string {
    if (!code) return "";
    const words = code.replace(/^CUSTOMER_/, "").toLowerCase().replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function initials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

const STATUS_COLOR: Record<Customer["status"], "success" | "warning" | "info"> = {
    ACTIVE: "success",
    INACTIVE: "warning",
    DUPLICATE: "info",
    MERGED: "info",
};

// ── Small building blocks ───────────────────────────────────────────────────

function Section({
    title,
    icon,
    action,
    children,
}: {
    title: string;
    icon: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Box component="section">
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    minHeight: 32,
                    mb: 1,
                }}
            >
                <Typography
                    component="h3"
                    variant="overline"
                    color="text.secondary"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        lineHeight: 1.6,
                        "& svg": { fontSize: 18 },
                    }}
                >
                    {icon}
                    {title}
                </Typography>
                {action}
            </Box>
            {children}
        </Box>
    );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
    return (
        <Typography variant="body2" color="text.secondary">
            {children}
        </Typography>
    );
}

function CopyButton({ value, label }: { value: string; label: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
            <IconButton
                size="small"
                aria-label={`Copy ${label}`}
                onClick={() => {
                    // Only claim "Copied" if the browser allowed it.
                    navigator.clipboard
                        ?.writeText(value)
                        .then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1500);
                        })
                        .catch(() => undefined);
                }}
            >
                {copied ? (
                    <CheckCircle fontSize="small" color="success" />
                ) : (
                    <ContentCopy fontSize="small" />
                )}
            </IconButton>
        </Tooltip>
    );
}

function ContactRow({
    icon,
    label,
    value,
    href,
    copy,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    /** tel:/mailto: link for the value. */
    href?: string;
    /** Show a copy button. */
    copy?: boolean;
}) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}>
            <Box sx={{ color: "action.active", display: "flex", flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {href ? (
                    <Link href={href} underline="hover" variant="body1" sx={{ wordBreak: "break-word" }}>
                        {value}
                    </Link>
                ) : (
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        {value}
                    </Typography>
                )}
            </Box>
            {copy && <CopyButton value={value} label={label.toLowerCase()} />}
        </Box>
    );
}

/** A label on the left, its value on the right. */
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 0.75,
            }}
        >
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Box sx={{ textAlign: "right", typography: "body2" }}>{children}</Box>
        </Box>
    );
}

function YesNo({ value }: { value: boolean }) {
    return value ? (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "success.main", fontWeight: 600 }}>
            <CheckCircle sx={{ fontSize: 18 }} /> Yes
        </Box>
    ) : (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
            <RemoveCircleOutline sx={{ fontSize: 18 }} /> No
        </Box>
    );
}

// Web addresses in free text (the "profile" field holds social links etc.).
const URL_PATTERN =
    /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9-]+\.(com|net|org|io|co|edu|gov|info|biz|me|app|dev|tech)[^\s]*)/gi;

/** Plain text with its web addresses turned into links, line breaks kept. */
function LinkifiedText({ text }: { text: string }) {
    const lines = text.split("\n");
    return (
        <>
            {lines.map((line, lineIndex) => {
                const parts: React.ReactNode[] = [];
                const pattern = new RegExp(URL_PATTERN.source, "gi");
                let last = 0;
                let match: RegExpExecArray | null;
                while ((match = pattern.exec(line)) !== null) {
                    if (match.index > last) parts.push(line.slice(last, match.index));
                    const url = match[0];
                    parts.push(
                        <Link
                            key={`${lineIndex}-${match.index}`}
                            href={url.startsWith("http") ? url : `https://${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ wordBreak: "break-all" }}
                        >
                            {url}
                        </Link>,
                    );
                    last = pattern.lastIndex;
                }
                if (last < line.length) parts.push(line.slice(last));
                return (
                    <React.Fragment key={lineIndex}>
                        {parts}
                        {lineIndex < lines.length - 1 && <br />}
                    </React.Fragment>
                );
            })}
        </>
    );
}

// ── The dialog ──────────────────────────────────────────────────────────────

/**
 * A single customer's full details — contact info, addresses (set default),
 * pets (add/edit/delete), preferences, account info, a password reset link,
 * a link to all their orders and (with `onEdit`) an Edit button. Shared by the
 * Customers page and the All Orders page; open it through
 * useCustomerDetails(), which loads fresh addresses and pets first.
 */
export const CustomerDetailsModal: React.FC<CustomerDetailsModalProps> = ({
    open,
    onClose,
    customer,
    onEdit,
    refreshing = false,
    onChange,
    onShowOrders,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { canWrite } = usePermissions();
    const titleId = useId();

    // Addresses live in their own state (seeded from `customer`, refreshed
    // after a set-default call) so switching the default doesn't require
    // the parent list to re-fetch the whole customer.
    const [addresses, setAddresses] = useState<CustomerAddress[]>(customer?.addresses || []);
    const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
    const [addressError, setAddressError] = useState("");

    useEffect(() => {
        setAddresses(customer?.addresses || []);
        setAddressError("");
    }, [customer]);

    // Pets live in their own state too (seeded from `customer`, refreshed
    // after any mutation) — same reasoning as addresses above.
    const [pets, setPets] = useState<Pet[]>(customer?.pets || []);
    const [petError, setPetError] = useState("");
    const [deletingPetId, setDeletingPetId] = useState<string | null>(null);
    const [petEditorOpen, setPetEditorOpen] = useState(false);
    const [editingPet, setEditingPet] = useState<Pet | null>(null);

    useEffect(() => {
        setPets(customer?.pets || []);
        setPetError("");
    }, [customer]);

    // Admin-generated password reset link — no OTP, no email delivery.
    // The URL is shown exactly once; staff copy it and send it to the
    // customer themselves (call, WhatsApp, however they just verified them).
    const [resetLinkLoading, setResetLinkLoading] = useState(false);
    const [resetLinkError, setResetLinkError] = useState("");
    const [resetLink, setResetLink] = useState<{
        open: boolean;
        url: string;
        expiresAt: string;
    }>({ open: false, url: "", expiresAt: "" });
    const [resetLinkCopied, setResetLinkCopied] = useState(false);

    if (!customer) return null;

    const fullName = getCustomerFullName(customer) || "Unnamed customer";
    // The customer exactly as shown right now (incl. changes made here).
    const current = (): Customer => ({ ...customer, addresses, pets });

    const refreshPets = async () => {
        try {
            const fresh = await petService.getCustomerPets(customer.customer_id);
            setPets(fresh);
            onChange?.({ ...customer, addresses, pets: fresh }, "pets");
        } catch (err: any) {
            setPetError(err.response?.data?.detail || "Failed to refresh pets");
        }
    };

    const handleAddPet = () => {
        setEditingPet(null);
        setPetEditorOpen(true);
    };

    const handleEditPet = (pet: Pet) => {
        setEditingPet(pet);
        setPetEditorOpen(true);
    };

    const handleDeletePet = async (pet: Pet) => {
        if (!window.confirm(`Remove ${pet.name} from this customer's pets?`)) return;
        setDeletingPetId(pet.pet_profile_id);
        setPetError("");
        try {
            await petService.deletePet(pet.pet_profile_id);
            await refreshPets();
        } catch (err: any) {
            setPetError(err.response?.data?.detail || "Failed to delete pet");
        } finally {
            setDeletingPetId(null);
        }
    };

    const handleGenerateResetLink = async () => {
        setResetLinkLoading(true);
        setResetLinkError("");
        try {
            const result = await customerService.generatePasswordResetLink(customer.customer_id);
            setResetLink({ open: true, url: result.reset_url, expiresAt: result.expires_at });
        } catch (err: any) {
            setResetLinkError(err.response?.data?.detail || "Failed to generate a reset link");
        } finally {
            setResetLinkLoading(false);
        }
    };

    const handleCopyResetLink = () => {
        navigator.clipboard.writeText(resetLink.url);
        setResetLinkCopied(true);
        setTimeout(() => setResetLinkCopied(false), 2000);
    };

    const closeResetLink = () => {
        setResetLink({ open: false, url: "", expiresAt: "" });
        setResetLinkCopied(false);
    };

    const handleSetDefault = async (addressId: string) => {
        setSettingDefaultId(addressId);
        setAddressError("");
        try {
            await customerService.setDefaultAddress(addressId);
            // set-default only flips is_default on rows sharing this one's
            // address_type — re-fetch the list rather than re-deriving
            // that demotion logic client-side.
            const fresh = await customerService.getCustomerAddresses(customer.customer_id);
            setAddresses(fresh);
            onChange?.({ ...customer, addresses: fresh, pets }, "addresses");
        } catch (err: any) {
            setAddressError(err.response?.data?.detail || "Failed to set default address");
        } finally {
            setSettingDefaultId(null);
        }
    };

    // Defaults first, otherwise in the order they were saved.
    const sortedAddresses = [...addresses].sort(
        (a, b) => Number(b.is_default) - Number(a.is_default),
    );
    const hasContact =
        !!customer.phone || !!customer.alternative_phone || !!customer.email || !!customer.date_of_birth;

    const ordersLabel = `All ${customer.first_name?.trim() || fullName}'s Orders`;
    const ordersButton = onShowOrders && (
        <Button
            variant="outlined"
            size="small"
            startIcon={<ReceiptLongOutlined />}
            onClick={() => onShowOrders(current())}
            title={ordersLabel}
            fullWidth={isMobile}
            sx={{ maxWidth: { xs: "100%", sm: 260 }, minWidth: 0 }}
        >
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ordersLabel}
            </Box>
        </Button>
    );

    // Phones get icon buttons to leave room for the name (the orders button
    // gets its own full-width row under the header there instead).
    const headerActions = (
        <>
            {!isMobile && ordersButton}
            {onEdit &&
                (isMobile ? (
                    <Tooltip title="Edit customer">
                        <IconButton aria-label="Edit customer" color="primary" onClick={() => onEdit(current())}>
                            <Edit />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button variant="contained" size="small" startIcon={<Edit />} onClick={() => onEdit(current())}>
                        Edit
                    </Button>
                ))}
        </>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                aria-labelledby={titleId}
            >
                {/* Header — stays in place while the details scroll */}
                <DialogTitle component="div" sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            py: 2,
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: "primary.main",
                                width: { xs: 44, sm: 52 },
                                height: { xs: 44, sm: 52 },
                                fontSize: { xs: 17, sm: 20 },
                                fontWeight: 600,
                            }}
                        >
                            {initials(fullName)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography id={titleId} component="h2" variant="h6" sx={{ lineHeight: 1.3, wordBreak: "break-word" }}>
                                {fullName}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 0.75 }}>
                                <Chip
                                    size="small"
                                    label={humanize(customer.status)}
                                    color={STATUS_COLOR[customer.status] ?? "default"}
                                />
                                <Chip size="small" variant="outlined" label={humanize(customer.source)} />
                                <Typography variant="caption" color="text.secondary">
                                    Customer since {formatDate(customer.create_time)}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            {headerActions}
                            <IconButton aria-label="Close" onClick={onClose} edge="end">
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                    {isMobile && ordersButton && <Box sx={{ px: 2, pb: 1.5 }}>{ordersButton}</Box>}
                    {/* Fixed-height slot, so the header doesn't jump. */}
                    <Box sx={{ height: 2 }}>{refreshing && <LinearProgress sx={{ height: 2 }} />}</Box>
                </DialogTitle>
                <Divider />

                <DialogContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
                            minHeight: "100%",
                        }}
                    >
                        {/* Left: how to reach them */}
                        <Box sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 3 }}>
                            <Section title="Contact" icon={<ContactPhoneOutlined />}>
                                {hasContact ? (
                                    <Box>
                                        {customer.phone && (
                                            <ContactRow
                                                icon={<PhoneOutlined />}
                                                label="Phone"
                                                value={customer.phone}
                                                href={`tel:${customer.phone}`}
                                                copy
                                            />
                                        )}
                                        {customer.alternative_phone && (
                                            <ContactRow
                                                icon={<PhoneOutlined />}
                                                label="Alternative phone"
                                                value={customer.alternative_phone}
                                                href={`tel:${customer.alternative_phone}`}
                                                copy
                                            />
                                        )}
                                        {customer.email && (
                                            <ContactRow
                                                icon={<MailOutline />}
                                                label="Email"
                                                value={customer.email}
                                                href={`mailto:${customer.email}`}
                                                copy
                                            />
                                        )}
                                        {customer.date_of_birth && (
                                            <ContactRow
                                                icon={<CakeOutlined />}
                                                label="Date of birth"
                                                value={formatDate(customer.date_of_birth)}
                                            />
                                        )}
                                    </Box>
                                ) : (
                                    <EmptyLine>No contact details saved.</EmptyLine>
                                )}
                            </Section>

                            <Section title="Addresses" icon={<HomeOutlined />}>
                                {addressError && (
                                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAddressError("")}>
                                        {addressError}
                                    </Alert>
                                )}
                                {sortedAddresses.length === 0 ? (
                                    <EmptyLine>No addresses saved.</EmptyLine>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                        {sortedAddresses.map((address) => (
                                            <Paper
                                                key={address.address_id}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    borderColor: address.is_default ? "primary.main" : "divider",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography variant="subtitle2">
                                                            {humanize(address.address_type)}
                                                        </Typography>
                                                        {address.is_default && (
                                                            <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                                                        )}
                                                    </Box>
                                                    {canWrite && !address.is_default && (
                                                        <Button
                                                            size="small"
                                                            disabled={settingDefaultId === address.address_id}
                                                            onClick={() => handleSetDefault(address.address_id)}
                                                        >
                                                            {settingDefaultId === address.address_id
                                                                ? "Setting…"
                                                                : "Set as default"}
                                                        </Button>
                                                    )}
                                                </Box>
                                                <Typography variant="body2">{address.street_address}</Typography>
                                                {address.city && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {address.city}
                                                    </Typography>
                                                )}
                                                {address.map_link && (
                                                    <Link
                                                        href={address.map_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        variant="body2"
                                                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.75 }}
                                                    >
                                                        <LocationOn sx={{ fontSize: 18 }} />
                                                        Open in Maps
                                                    </Link>
                                                )}
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Section>

                            {customer.note && (
                                <Section title="Notes" icon={<NotesOutlined />}>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {customer.note}
                                    </Typography>
                                </Section>
                            )}

                            {customer.profile && (
                                <Section title="Profile & links" icon={<LinkOutlined />}>
                                    <Typography variant="body2" component="div" sx={{ wordBreak: "break-word" }}>
                                        <LinkifiedText text={customer.profile} />
                                    </Typography>
                                </Section>
                            )}
                        </Box>

                        {/* Right: pets, preferences, account */}
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3 },
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                                bgcolor: "grey.50",
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Section
                                title="Pets"
                                icon={<PetsOutlined />}
                                action={
                                    canWrite && (
                                        <Button size="small" startIcon={<Add />} onClick={handleAddPet}>
                                            Add
                                        </Button>
                                    )
                                }
                            >
                                {petError && (
                                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPetError("")}>
                                        {petError}
                                    </Alert>
                                )}
                                {pets.length === 0 ? (
                                    <EmptyLine>No pets yet.</EmptyLine>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {pets.map((pet) => (
                                            <Box
                                                key={pet.pet_profile_id}
                                                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                                            >
                                                <Avatar src={pet.photo_url} sx={{ width: 40, height: 40 }}>
                                                    <PetsOutlined fontSize="small" />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" noWrap>
                                                        {pet.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                                                        {[
                                                            pet.species,
                                                            pet.breed,
                                                            pet.gender === "female" ? "Female" : pet.gender === "male" ? "Male" : null,
                                                            pet.weight_kg != null ? `${Number(pet.weight_kg)} kg` : null,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </Typography>
                                                    {pet.notes && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: "block", fontStyle: "italic", wordBreak: "break-word" }}
                                                        >
                                                            {pet.notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {canWrite && (
                                                    <Box sx={{ display: "flex", flexShrink: 0 }}>
                                                        <Tooltip title={`Edit ${pet.name}`}>
                                                            <IconButton size="small" onClick={() => handleEditPet(pet)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={`Remove ${pet.name}`}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={deletingPetId === pet.pet_profile_id}
                                                                    onClick={() => handleDeletePet(pet)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Section>

                            <LinkedTasks
                                kind="customer"
                                id={customer.customer_id}
                                label={[customer.first_name, customer.last_name].filter(Boolean).join(" ")}
                                heading="overline"
                            />

                            <Section title="Preferences" icon={<TuneOutlined />}>
                                <InfoRow label="Marketing messages">
                                    <YesNo value={customer.marketing_consent} />
                                </InfoRow>
                                <InfoRow label="Newsletter">
                                    <YesNo value={customer.newsletter_subscription} />
                                </InfoRow>
                                {customer.preferred_contact_method && (
                                    <InfoRow label="Prefers contact by">
                                        {humanize(customer.preferred_contact_method)}
                                    </InfoRow>
                                )}
                            </Section>

                            <Section title="Account" icon={<ManageAccountsOutlined />}>
                                <InfoRow label="Phone verified">
                                    <YesNo value={customer.is_phone_verified} />
                                </InfoRow>
                                <InfoRow label="Email verified">
                                    <YesNo value={customer.is_email_verified} />
                                </InfoRow>
                                <InfoRow label="Last login">{formatDate(customer.last_login)}</InfoRow>
                                <InfoRow label="Last order">{formatDate(customer.last_order_date)}</InfoRow>
                                {canWrite && (
                                    <Box sx={{ mt: 1.5 }}>
                                        {resetLinkError && (
                                            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setResetLinkError("")}>
                                                {resetLinkError}
                                            </Alert>
                                        )}
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<LockReset />}
                                            disabled={resetLinkLoading}
                                            onClick={handleGenerateResetLink}
                                        >
                                            {resetLinkLoading ? "Generating…" : "Reset password link"}
                                        </Button>
                                    </Box>
                                )}
                            </Section>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <PetProfileEditor
                open={petEditorOpen}
                onClose={() => setPetEditorOpen(false)}
                customerId={customer.customer_id}
                pet={editingPet}
                onSaved={() => refreshPets()}
            />

            <Dialog open={resetLink.open} onClose={closeResetLink} maxWidth="sm" fullWidth>
                <DialogTitle>Reset Link Ready</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Copy this now and send it to the customer yourself — by phone,
                        WhatsApp, or however you just verified them. It will not be
                        shown again, and expires{" "}
                        {resetLink.expiresAt && new Date(resetLink.expiresAt).toLocaleString()}.
                    </Alert>
                    <TextField
                        fullWidth
                        value={resetLink.url}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={resetLinkCopied ? "Copied!" : "Copy"}>
                                        <IconButton onClick={handleCopyResetLink} edge="end">
                                            <ContentCopy fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select?.()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeResetLink} variant="contained">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
